import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { nfseService, type CriarNfseRequest, type ListaNfseFiltros } from '../services/nfseService';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';

const KEYS = {
  all:     ['nfse'] as const,
  lista:   (p: number, t: number, filtros: ListaNfseFiltros) => ['nfse', 'lista', p, t, filtros] as const,
  detalhe: (id: number)           => ['nfse', id] as const,
};

export const useNfseList = (pagina = 1, tamanho = 30, filtros: ListaNfseFiltros = {}) => {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: KEYS.lista(pagina, tamanho, filtros),
    queryFn:  () => nfseService.listar(pagina, tamanho, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada letra da busca
  });

  const transmitirMutation = useMutation({
    mutationFn: (id: number) => nfseService.transmitir(id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      toast.success(res.motivo ?? 'Transmitido');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const cancelarMutation = useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) =>
      nfseService.cancelar(id, motivo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      toast.success('NFS-e cancelada');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return {
    ...query,
    notas:     query.data?.items ?? [],
    total:     query.data?.total ?? 0,
    transmitir: transmitirMutation.mutate,
    cancelar:   cancelarMutation.mutate,
    isTransmitindo: transmitirMutation.isPending,
    isCancelando:   cancelarMutation.isPending,
  };
};

export const useNfseDetalhe = (id: number) =>
  useQuery({
    queryKey: KEYS.detalhe(id),
    queryFn:  () => nfseService.detalhe(id),
    enabled:  id > 0,
  });

export const useCriarNfse = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CriarNfseRequest) => nfseService.criar(req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
};
